import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

/**
 * The collection book.
 *
 * Each card is a real concept with flavour text that states it plainly, so the
 * collection doubles as a revision index — the reward for beating a floor is
 * also a one-line summary of what that floor argued.
 *
 * Unowned cards are shown as silhouettes rather than hidden. A locked card you
 * can see is motivating; one you cannot is merely absent.
 */

interface Artifact {
  id: string
  floor: number
  name: string
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  flavor_text: string
  zone: string | null
  color: string | null
  owned: number
  granted_at: number | null
}

export default function Collection() {
  const [artifacts, setArtifacts] = useState<Artifact[] | null>(null)

  useEffect(() => {
    api<{ artifacts: Artifact[] }>('/game/collection')
      .then((d) => setArtifacts(d.artifacts))
      .catch(() => setArtifacts([]))
  }, [])

  if (!artifacts) return <div className="loading">Opening the collection…</div>

  const owned = artifacts.filter((a) => a.owned).length

  return (
    <div className="collection">
      <header className="hero">
        <h1>Collection</h1>
        <p className="hero-sub">
          One artifact per floor, dropped when you defeat its boss. {owned} of {artifacts.length}{' '}
          recovered.
        </p>
      </header>

      <div className="card-grid">
        {artifacts.map((a) => (
          <article
            key={a.id}
            className={`art-card rarity-${a.rarity} ${a.owned ? '' : 'art-locked'}`}
            style={a.owned && a.color ? { borderColor: a.color } : undefined}
          >
            <div className="art-floor">Floor {a.floor}</div>
            {a.owned ? (
              <>
                <h2>{a.name}</h2>
                <p className="art-zone dim">{a.zone}</p>
                <p className="artifact-flavor">“{a.flavor_text}”</p>
                <span className={`rarity-tag rarity-${a.rarity}`}>{a.rarity}</span>
              </>
            ) : (
              <>
                <h2 className="art-hidden">? ? ?</h2>
                <p className="art-zone dim">{a.zone ?? 'Unbuilt floor'}</p>
                <p className="dim art-locked-note">
                  {a.zone ? (
                    <>
                      Defeat this floor's boss to recover it.
                    </>
                  ) : (
                    'This floor is not built yet.'
                  )}
                </p>
              </>
            )}
          </article>
        ))}
      </div>

      <p className="dim">
        <Link to="/">← Back to the tower</Link>
      </p>
    </div>
  )
}
